import React, { useEffect, useState } from "react";
import clsx from "clsx";
import useSWR from "swr";
import SearchIcon from "@material-ui/icons/Search";

import KPILeaderboardTable from "./components/KPILeaderboardTable";
// import KPIToggleTabs from "./components/KPIToggleTabs";
import "./index.scss";
import SyncIcon from "../../img/sync.svg";
import {
  geoHierarchy,
  getHierarchyListByUserType,
  getPluralizedLabel,
} from "./utils/kpiDashboardHelper";
import KPIParametersSummaryTable from "./components/KPIParametersSummaryTable";
import GeoFilters from "./components/GeoFilters";
import { KPIDashboardService } from "./service/kpiDashboardService";
import SearchBar from "../Shared/SearchBar/SearchBar";
// import ErrorCard from "../InventoryManagement/components/Common/ErrorCard";
import useDebounce from "../Shared/GeographyFilters/hooks/useDebounce";
import useQueryParams from "../Shared/Hooks/useQueryParms";
import { Redirect } from "react-router";

const INITIAL_GEO_FILTERS = {
  circle: null,
  zone: null,
  area: null,
  territory: null,
  agency: null,
};

const KPILeaderboard = () => {
  const [searchText, setSearchText] = useState("");
  const debouncedSearchText = useDebounce(searchText, 400);
  const leaderboardQuery = searchText === "" ? "" : debouncedSearchText;
  const [filterSummary, setFilterSummary] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [geoFilters, setGeoFilters] = useState(INITIAL_GEO_FILTERS);
  const userType = JSON.parse(localStorage.getItem("currUserType"));
  const filteredGeoEntityTabs = getHierarchyListByUserType(userType);

  const KPI_QUERY_SCHEMA = {
    geo_entity: {
      defaultValue: null,
      permittedValues: filteredGeoEntityTabs
        .map((t) => t.value)
        .filter(Boolean),
    },
    kpi_parameter: {
      defaultValue: null,
    },
  };

  const { params, setParam, setParams } = useQueryParams(KPI_QUERY_SCHEMA);
  const selectedGeoEntity = params.geo_entity;
  const selectedKPIParameter = params.kpi_parameter;

  const { data: metricsData, isValidating: isMetricsLoading } = useSWR(
    "kpi-metrics-list",
    () => KPIDashboardService.metricListFetcher(),
    { revalidateOnFocus: false, revalidateOnReconnect: false }
  );

  useEffect(() => {
    resetGeoFilters();
  }, [selectedGeoEntity]);

  const localUserToken = JSON.parse(localStorage.getItem("currUserToken"));
  if (!localUserToken) return <Redirect to="/signin" />;

  const kpiParametersOptions = [
    { label: "Overall", value: null },
    ...(metricsData?.metrics || []).map((m) => ({
      label: m.label,
      value: m.metrics,
    })),
  ];

  const isGeoTabSelected = selectedGeoEntity !== null;
  const leaderboardTableKey = `${selectedGeoEntity || "none"}::${JSON.stringify(
    selectedKPIParameter
  )}`;

  const handleGeoEntityTabClick = (geoEntity) => {
    setSearchText("");
    setParams({ geo_entity: geoEntity, kpi_parameter: null });
  };

  const handleKPIParameterTabClick = (kpiParameter) => {
    setSearchText("");
    setParam("kpi_parameter", kpiParameter);
  };

  const resetGeoFilters = () => {
    setGeoFilters(INITIAL_GEO_FILTERS);
    setFilterSummary("");
  };

  const handleGeoFilterChange = (newFilters, summaryText) => {
    setGeoFilters(newFilters);
    setFilterSummary(summaryText || "");
  };

  const getFilterText = () => {
    if (selectedGeoEntity === null) return "all of my parameters";

    const selectedGeoEntityLabel = filteredGeoEntityTabs.find(
      (tab) => tab.value === selectedGeoEntity
    )?.label;

    const hasActiveFilters = Object.values(geoFilters).some(Boolean);
    if (!hasActiveFilters) {
      return `all ${selectedGeoEntityLabel}`;
    }

    if (filterSummary) {
      const countMatch = filterSummary.match(/^(\d+)/);
      const count = countMatch ? parseInt(countMatch[1]) : 1;
      const pluralizedEntity = getPluralizedLabel(
        selectedGeoEntityLabel,
        count
      );
      return `${pluralizedEntity} from ${filterSummary}`;
    }

    return `all ${selectedGeoEntityLabel}`;
  };

  return (
    <div className="main-container">
      <div className="kpi-page-header">
        <div className="page-title">
          <h1>MDT KPI Tracking Dashboard </h1>
          <div className="last-updated-info">
            <img src={SyncIcon} alt="sync" />{" "}
            <span>Last updated: {lastUpdated || "-"}</span>
          </div>
        </div>
        <div className="header-controls">
          {geoHierarchy[selectedGeoEntity] > geoHierarchy["zone"] && (
            <SearchBar
              icon=<SearchIcon style={{ color: "#000E3D" }} />
              value={searchText}
              onSearch={setSearchText}
              placeholder="Search.."
              style={{
                width: "250px",
                maxHeight: "55px",
                margin: 0,
                padding: "6px 16px",
                border: "1px solid #F2F2F2B8",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            />
          )}
          {/* <KPIToggleTabs onTabChange={() => setSearchText("")} /> */}
        </div>
      </div>

      <div className="kpi-page-content">
        <div className="kpi-tabs-container">
          {filteredGeoEntityTabs.map((tab, index) => (
            <button
              key={index}
              onClick={() => handleGeoEntityTabClick(tab.value)}
              className={clsx(
                "filter-tab",
                selectedGeoEntity === tab.value && "active"
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {isGeoTabSelected && (
          <div className="kpi-tabs-container">
            {isMetricsLoading && !metricsData
              ? [...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    style={{
                      borderRadius: "24px",
                      padding: "8px 16px",
                      width: "100px",
                      height: "32px",
                      backgroundColor: "#E0E0E0",
                    }}
                  />
                ))
              : kpiParametersOptions.map((option, index) => (
                  <button
                    key={index}
                    className={clsx(
                      "filter-tab",
                      selectedKPIParameter === option.value && "active"
                    )}
                    onClick={() => handleKPIParameterTabClick(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
          </div>
        )}
        <div className="geo-filter-bar">
          <h2>Viewing {getFilterText()}</h2>
          {isGeoTabSelected && (
            <GeoFilters
              selectedGeoEntity={selectedGeoEntity}
              geoFilters={geoFilters}
              onFilterChange={handleGeoFilterChange}
              resetGeoFilters={resetGeoFilters}
            />
          )}
        </div>
        {selectedGeoEntity === null ? (
          <KPIParametersSummaryTable
            userId={null}
            onGeoBreakdownView={handleGeoEntityTabClick}
            setLastUpdated={setLastUpdated}
          />
        ) : (
          <KPILeaderboardTable
            key={leaderboardTableKey}
            hierarchy={selectedGeoEntity}
            metric={selectedKPIParameter}
            query={leaderboardQuery}
            geoFilters={geoFilters}
            setLastUpdated={setLastUpdated}
          />
        )}
      </div>
    </div>
  );
};

export default KPILeaderboard;
